import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import './ContactModal.css';

// Indian states and their cities data
const indianStates = {
    'Gujarat': ['Surat', 'Ahmedabad', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar', 'Gandhinagar', 'Junagadh', 'Anand', 'Navsari'],
    'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Kolhapur'],
    'Rajasthan': ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Bikaner', 'Ajmer', 'Bhilwara', 'Alwar'],
    'Karnataka': ['Bengaluru', 'Mysuru', 'Hubli', 'Mangaluru', 'Belgaum', 'Gulbarga'],
    'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli'],
    'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Ghaziabad', 'Agra', 'Varanasi', 'Meerut'],
    'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri'],
    'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar'],
    'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Nellore', 'Kurnool'],
    'Kerala': ['Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur', 'Kollam'],
    'Madhya Pradesh': ['Bhopal', 'Indore', 'Jabalpur', 'Gwalior', 'Ujjain'],
    'Punjab': ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda'],
    'Haryana': ['Faridabad', 'Gurgaon', 'Panipat', 'Ambala', 'Yamunanagar'],
    'Delhi': ['New Delhi', 'Delhi', 'Dwarka', 'Rohini', 'Pitampura'],
    'Goa': ['Panaji', 'Margao', 'Vasco da Gama', 'Mapusa', 'Ponda']
};

const ContactModal = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        country: 'India',
        state: '',
        city: '',
        location: '',
        service: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');

    // Get cities based on selected state
    const availableCities = useMemo(() => {
        if (formData.country === 'India' && formData.state) {
            return indianStates[formData.state] || [];
        }
        return [];
    }, [formData.country, formData.state]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'state') {
            setFormData({
                ...formData,
                state: value,
                city: ''
            });
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const result = await emailjs.send(
                import.meta.env.VITE_EMAILJS_SERVICE_ID,
                import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
                formData,
                import.meta.env.VITE_EMAILJS_PUBLIC_KEY
            );

            if (result.status === 200) {
                setSubmitMessage('Thank you! Your message has been sent successfully.');
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    country: 'India',
                    state: '',
                    city: '',
                    location: '',
                    service: ''
                });
                setTimeout(() => {
                    onClose();
                    setSubmitMessage('');
                }, 2000);
            }
        } catch (error) {
            setSubmitMessage('Sorry, there was an error. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="modal-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        className="modal-content"
                        initial={{ opacity: 0, scale: 0.9, y: 50 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 50 }}
                        onClick={e => e.stopPropagation()}
                    >
                        <button className="modal-close" onClick={onClose}>
                            <i className="bi bi-x-lg"></i>
                        </button>

                        <h2 className="modal-title">Get In Touch</h2>
                        <p className="modal-subtitle">Fill out the form below and we'll get back to you soon</p>

                        <form className="modal-form" onSubmit={handleSubmit}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="modal-name">Name *</label>
                                    <input
                                        type="text"
                                        id="modal-name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="modal-email">Email *</label>
                                    <input
                                        type="email"
                                        id="modal-email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="modal-phone">Phone *</label>
                                    <input
                                        type="tel"
                                        id="modal-phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="modal-country">Country *</label>
                                    <select
                                        id="modal-country"
                                        name="country"
                                        value={formData.country}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="India">India</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="modal-state">State *</label>
                                    <select
                                        id="modal-state"
                                        name="state"
                                        value={formData.state}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">-- Select State --</option>
                                        {Object.keys(indianStates).sort().map(state => (
                                            <option key={state} value={state}>{state}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="modal-city">City *</label>
                                    <select
                                        id="modal-city"
                                        name="city"
                                        value={formData.city}
                                        onChange={handleChange}
                                        required
                                        disabled={!formData.state}
                                    >
                                        <option value="">-- Select City --</option>
                                        {availableCities.map(city => (
                                            <option key={city} value={city}>{city}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-group full-width">
                                <label htmlFor="modal-location">Location / Address Details *</label>
                                <input
                                    type="text"
                                    id="modal-location"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleChange}
                                    placeholder="Area, Landmark, Street etc."
                                    required
                                />
                            </div>

                            <div className="form-group full-width">
                                <label htmlFor="modal-service">Service Type *</label>
                                <select
                                    id="modal-service"
                                    name="service"
                                    value={formData.service}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">-- Select Service --</option>
                                    <option value="Residential bungalows">Residential bungalows, Farm houses</option>
                                    <option value="Commercial spaces">Commercial and educational spaces</option>
                                    <option value="Institutions">Institution, Hospitals and hotels</option>
                                    <option value="Multi-storey">Multi-storey buildings (5+ storeys)</option>
                                    <option value="Structural">Structural consultancy</option>
                                    <option value="3D visualization">3D view for exterior and interior</option>
                                </select>
                            </div>

                            <button
                                type="submit"
                                className="submit-button"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Sending...' : 'Send Message'}
                            </button>

                            {submitMessage && (
                                <div className={`submit-message ${submitMessage.includes('error') ? 'error' : 'success'}`}>
                                    {submitMessage}
                                </div>
                            )}
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default ContactModal;
